/**
 * Notification Service
 * Handles all notification-related operations
 */

import { supabase } from '../lib/supabase'

/**
 * Notification types for consistent usage across the app
 */
export const NOTIFICATION_TYPES = {
  PROJECT_CREATED: 'project_created',
  PROJECT_UPDATED: 'project_updated',
  VERSION_UPLOADED: 'version_uploaded',
  COMMENT_ADDED: 'comment_added',
  APPROVAL_RECEIVED: 'approval_received',
  CHANGES_REQUESTED: 'changes_requested',
  TEAM_MEMBER_ADDED: 'team_member_added',
  TEAM_MEMBER_REMOVED: 'team_member_removed',
  INVITATION_RECEIVED: 'invitation_received',
  SUBSCRIPTION_UPDATED: 'subscription_updated',
  STORAGE_WARNING: 'storage_warning',
  GENERAL: 'general'
}

/**
 * Get notification icon based on type
 */
export const getNotificationIcon = (type) => {
  const icons = {
    project_created: 'folder_open',
    project_updated: 'edit_note',
    version_uploaded: 'upload_file',
    comment_added: 'comment',
    approval_received: 'check_circle',
    changes_requested: 'edit',
    team_member_added: 'person_add',
    team_member_removed: 'person_remove',
    invitation_received: 'mail',
    subscription_updated: 'payments',
    storage_warning: 'warning',
    general: 'notifications'
  }
  return icons[type] || 'notifications'
}

/**
 * Get notification color based on type
 */
export const getNotificationColor = (type) => {
  const colors = {
    project_created: 'text-blue-500',
    project_updated: 'text-indigo-500',
    version_uploaded: 'text-purple-500',
    comment_added: 'text-cyan-500',
    approval_received: 'text-green-500',
    changes_requested: 'text-yellow-500',
    team_member_added: 'text-emerald-500',
    team_member_removed: 'text-red-500',
    invitation_received: 'text-pink-500',
    subscription_updated: 'text-violet-500',
    storage_warning: 'text-orange-500',
    general: 'text-gray-500'
  }
  return colors[type] || 'text-gray-500'
}

/**
 * Get user notifications with pagination
 */
export const getUserNotifications = async (userId, { limit = 50, offset = 0, unreadOnly = false } = {}) => {
  try {
    let query = supabase
      .from('notifications')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (unreadOnly) {
      query = query.eq('read', false)
    }

    const { data, error, count } = await query

    if (error) throw error

    return { 
      data: data || [], 
      count, 
      error: null 
    }
  } catch (error) {
    console.error('Error fetching notifications:', error)
    return { data: [], count: 0, error }
  }
}

/**
 * Get unread notification count
 */
export const getUnreadCount = async (userId) => {
  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('read', false)

    if (error) throw error

    return { count: count || 0, error: null }
  } catch (error) {
    console.error('Error fetching unread count:', error)
    return { count: 0, error }
  }
}

/**
 * Mark notification as read
 */
export const markAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error marking notification as read:', error)
    return { data: null, error }
  }
}

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false)
      .select()

    if (error) throw error

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error marking all as read:', error)
    return { data: [], error }
  }
}

/**
 * Delete notification
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)

    if (error) throw error

    return { error: null }
  } catch (error) {
    console.error('Error deleting notification:', error)
    return { error }
  }
}

/**
 * Delete all read notifications
 */
export const deleteAllRead = async (userId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', userId)
      .eq('read', true)

    if (error) throw error

    return { error: null }
  } catch (error) {
    console.error('Error deleting read notifications:', error)
    return { error }
  }
}

/**
 * Create a notification manually (for system events)
 */
export const createNotification = async ({
  userId,
  organizationId = null,
  type = NOTIFICATION_TYPES.GENERAL,
  title,
  message,
  actionUrl = null,
  actionLabel = null,
  metadata = {}
}) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .insert([{
        user_id: userId,
        organization_id: organizationId,
        type,
        title,
        message,
        action_url: actionUrl,
        action_label: actionLabel,
        metadata
      }])
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error creating notification:', error)
    return { data: null, error }
  }
}

/**
 * Subscribe to real-time notifications for a user
 */
export const subscribeToNotifications = (userId, callback) => {
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new)
      }
    )
    .subscribe()

  return channel
}

/**
 * Unsubscribe from notifications
 */
export const unsubscribeFromNotifications = (channel) => {
  if (channel) {
    supabase.removeChannel(channel)
  }
}

/**
 * Format notification time (relative time)
 */
export const formatNotificationTime = (timestamp) => {
  const now = new Date()
  const notificationDate = new Date(timestamp)
  const diffInSeconds = Math.floor((now - notificationDate) / 1000)

  if (diffInSeconds < 60) {
    return 'Just now'
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours}h ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays}d ago`
  }

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return `${diffInWeeks}w ago`
  }

  // Return formatted date for older notifications
  return notificationDate.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: notificationDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

/**
 * Batch mark notifications as read
 */
export const batchMarkAsRead = async (notificationIds) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ read: true })
      .in('id', notificationIds)
      .select()

    if (error) throw error

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error batch marking as read:', error)
    return { data: [], error }
  }
}

/**
 * Get notifications by type
 */
export const getNotificationsByType = async (userId, type, limit = 20) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('type', type)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error

    return { data: data || [], error: null }
  } catch (error) {
    console.error('Error fetching notifications by type:', error)
    return { data: [], error }
  }
}
